"use client";

import { type ButtonHTMLAttributes, type MouseEvent, forwardRef } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { ArrowLeftOutlined } from "@ant-design/icons";
import { type VariantProps, tv } from "tailwind-variants";

import { cn } from "@/lib/utils";

export const buttonVariant = tv({
  base: "flex cursor-pointer flex-row items-center gap-4 self-start rounded-full border-0 bg-transparent px-0 font-poppins text-base font-normal text-raven-600 disabled:cursor-not-allowed disabled:text-gray-300 [&:disabled>span]:text-gray-400",
  variants: {
    variant: {
      primary: "gap-4 border-0 font-poppins text-base text-raven-600",
      secondary:
        "gap-[10px] border border-solid border-aubergine-perl-500 px-4 py-1 font-roboto text-sm leading-[22px] text-aubergine-perl-500",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

export type ButtonVariantProps = VariantProps<typeof buttonVariant>;

interface ButtonArrowBackProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariantProps["variant"];
  route?: string;
  label?: string;
}

const ButtonArrowBack = forwardRef<HTMLButtonElement, ButtonArrowBackProps>(
  (
    { className, route, onClick, label: text, variant = "primary", ...props },
    ref,
  ) => {
    const router = useRouter();
    const searchParams = useSearchParams();

    const push = !!searchParams.get("push");
    const accordion = searchParams.get("accordion");
    const destinationRoute = push ? "/push" : route;

    const handleClick = (
      event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
    ) => {
      if (onClick) onClick(event);
      if (destinationRoute) {
        router.push(
          destinationRoute + (accordion ? "?accordion=" + accordion : ""),
        );
      } else {
        router.back();
      }
    };

    return (
      <button
        ref={ref}
        className={cn(buttonVariant({ variant }), className)}
        onClick={handleClick}
        {...props}
      >
        <ArrowLeftOutlined className="text-brand-primary" /> {text ?? "Voltar"}
      </button>
    );
  },
);

ButtonArrowBack.displayName = "ButtonArrowBack"; // Add display name

export default ButtonArrowBack;
